/**
 * Grade Field Registry
 * Single source of truth for all Grade field definitions
 * Used by adapters to generate DataGrid columns, Advanced Search configs, and Modal fields
 */

import { Grade } from '../../services/api/domains/grades/types';
import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';

// Type alias for type-safe field definitions
type GradeFieldDefinition = BaseFieldDefinition<Grade>;

/**
 * Complete Grade Field Registry
 * Order matches typical display priority
 *
 * NOTE: The following fields are intentionally excluded or have special handling:
 * - grade_id: Auto-generated, used as rowId, shown only in modal view mode
 */
export const GRADE_FIELDS_REGISTRY: GradeFieldDefinition[] = [
  // ========== Core Information ==========
  {
    key: 'grade_name',
    label: 'Product Type',
    dataType: 'string',
    isRequired: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    validation: {
      minLength: 1,
      maxLength: 255,
    },
    renderConfig: {
      flex: 1,
      minWidth: 200,
    },
  },

  // ========== Metadata Fields ==========
  {
    key: 'user_id',
    label: 'User ID',
    dataType: 'string',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 200,
    },
  },
  {
    key: 'created_date',
    label: 'Created Date',
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
];

// Create field helpers using shared utility
const helpers = createFieldHelpers<Grade>(GRADE_FIELDS_REGISTRY);

export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
